import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, getUserId, API } from "@/lib/api";
import { categoryColor, VERSO_URL } from "@/data/constants";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, RotateCcw } from "lucide-react";
import { VoiceRecorder, AudioPlayer, VoiceJourney } from "@/components/Voice";

export default function CarnetReader() {
  const { id } = useParams();
  const navigate = useNavigate();
  const userId = getUserId();

  const [book, setBook] = useState(null);
  const [days, setDays] = useState([]);
  const [progress, setProgress] = useState({ current_day: 1, completed_days: [] });
  const [current, setCurrent] = useState(1);
  const [flipped, setFlipped] = useState(false);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [recMeta, setRecMeta] = useState([]);
  const [reminderEmail, setReminderEmail] = useState("");
  const [reminderSaved, setReminderSaved] = useState(false);
  const [demoSrc, setDemoSrc] = useState(`${API}/settings/demo-jour1`);

  useEffect(() => {
    Promise.all([
      api.get(`/books/${id}`),
      api.get(`/books/${id}/days`),
      api.get(`/progress/${userId}/${id}`),
      api.get(`/journal/${userId}/${id}`),
      api.get(`/recordings/${userId}/${id}`),
    ])
      .then(([bRes, dRes, pRes, jRes, rRes]) => {
        setBook(bRes.data);
        setDays(dRes.data);
        setProgress(pRes.data);
        setRecMeta(rRes.data || []);
        const startDay = pRes.data.current_day || 1;
        setCurrent(startDay);
        window.__journal = {};
        jRes.data.forEach((e) => (window.__journal[e.day_number] = e.response));
      })
      .finally(() => setLoading(false));
  }, [id, userId]);

  useEffect(() => {
    setFlipped(false);
    setNote((window.__journal && window.__journal[current]) || "");
  }, [current]);

  const day = days.find((d) => d.day_number === current);
  const completed = progress.completed_days || [];
  const isDone = completed.includes(current);
  const color = day ? categoryColor(day.category) : "#385E3A";
  const maxCompleted = completed.length ? Math.max(...completed) : 0;
  const unlockedUpTo = Math.min(21, maxCompleted + 1);
  const hasDay1 = recMeta.some((r) => r.slot === "day1");
  const hasDay21 = recMeta.some((r) => r.slot === "day21");
  const refreshRec = async () => {
    const r = await api.get(`/recordings/${userId}/${id}`);
    setRecMeta(r.data || []);
  };
  const saveReminder = async () => {
    try {
      await api.post("/reminder", { user_id: userId, book_id: id, email: reminderEmail });
      setReminderSaved(true);
      toast.success("Rappel programmé pour le Jour 21");
    } catch {
      toast.error("E-mail invalide");
    }
  };

  const saveNote = async () => {
    await api.post("/journal", {
      user_id: userId,
      book_id: id,
      day_number: current,
      response: note,
    });
    if (!window.__journal) window.__journal = {};
    window.__journal[current] = note;
    toast.success("Réponse enregistrée");
  };

  const finishDay = async () => {
    if (note.trim()) await saveNote();
    const r = await api.post("/progress", { user_id: userId, book_id: id, day: current });
    setProgress(r.data);
    toast.success(`Jour ${current} terminé`);
    if (current < 21) setCurrent(current + 1);
  };

  if (loading) return <div className="max-w-3xl mx-auto px-4 py-20 text-center font-sans text-[#1F2A24]/60">Chargement…</div>;
  if (!book) return <div className="max-w-3xl mx-auto px-4 py-20 text-center">Carnet introuvable.</div>;

  const pct = Math.round((completed.length / 21) * 100);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-8 py-10 md:py-14">
      <Link to="/carnets" data-testid="reader-back" className="inline-flex items-center gap-2 font-sans text-sm text-[#1F2A24]/60 hover:text-[#385E3A] transition-colors">
        <ArrowLeft size={16} /> Les Carnets
      </Link>

      <h1 className="font-serif text-3xl md:text-4xl text-[#1F2A24] mt-4">{book.title}</h1>

      {/* progress */}
      <div className="mt-6">
        <div className="flex justify-between font-sans text-sm text-[#1F2A24]/70">
          <span data-testid="reader-progress-label">Jour {current} / 21</span>
          <span>{completed.length} / 21 terminés</span>
        </div>
        <div className="h-2 rounded-full bg-[#E8DCC4] mt-2 overflow-hidden">
          <div data-testid="reader-progress-bar" className="h-full bg-[#C9A24B] transition-all duration-500" style={{ width: `${pct}%` }} />
        </div>
      </div>

      {/* day nav */}
      <div className="flex items-center justify-between mt-6">
        <button
          data-testid="reader-prev-day"
          disabled={current <= 1}
          onClick={() => setCurrent((c) => Math.max(1, c - 1))}
          className="inline-flex items-center gap-1 font-ui text-sm text-[#1F2A24] disabled:opacity-30"
        >
          <ArrowLeft size={16} /> Jour précédent
        </button>
        <button
          data-testid="reader-next-day"
          disabled={current >= 21 || current >= unlockedUpTo}
          onClick={() => setCurrent((c) => Math.min(unlockedUpTo, 21, c + 1))}
          className="inline-flex items-center gap-1 font-ui text-sm text-[#1F2A24] disabled:opacity-30"
        >
          Jour suivant <ArrowRight size={16} />
        </button>
      </div>

      {(current === 7 || current === 14) && (
        <div data-testid={`bilan-${current}`} className="mt-6 rounded-xl border border-[#C9A24B]/50 bg-[#1F2A24] text-[#F6F2E9] p-4 text-center">
          <span className="font-ui text-[11px] tracking-[0.25em] text-[#C9A24B]">JOUR {current}</span>
          <p className="font-serif text-xl mt-1">
            {current === 7 ? "Mon premier bilan" : "Je regarde plus profondément"}
          </p>
        </div>
      )}

      {/* card */}
      {day && (
        <div className="mt-6">
          <div
            data-testid="reader-card"
            className={`flip-card ${flipped ? "is-flipped" : ""} w-full max-w-[300px] aspect-[3/5] mx-auto`}
            onClick={() => setFlipped((f) => !f)}
          >
            <div className="flip-inner">
              {/* closed = verso officiel */}
              <div className="flip-face">
                <img src={VERSO_URL} alt="Dos de carte" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-x-0 bottom-0 p-4 flex justify-center bg-gradient-to-t from-black/45 to-transparent">
                  <span className="font-sans text-xs tracking-[0.2em] text-white inline-flex items-center gap-2">
                    <RotateCcw size={14} /> TOUCHER POUR OUVRIR
                  </span>
                </div>
              </div>
              {/* open */}
              <div className="flip-face flip-back bg-white border-2 p-6 flex flex-col justify-center text-center" style={{ borderColor: color }}>
                <span className="font-ui text-[11px] tracking-widest" style={{ color }}>{day.category}</span>
                <h3 className="font-serif text-2xl text-[#1F2A24] mt-2">{day.title}</h3>
                <div className="gold-divider w-16 mx-auto my-4" />
                <p className="font-serif italic text-lg text-[#1F2A24]/85">{day.question}</p>
              </div>
            </div>
          </div>

          {(day.exercise || day.action) && (
            <div className="grid gap-4 mt-6">
              {day.exercise && <Block label="EXPÉRIENCE" text={day.exercise} color={color} />}
              {day.action && <Block label="ACTION" text={day.action} color={color} />}
            </div>
          )}

          {current === 1 && (
            <div data-testid="rendezvous-day1" className="mt-8 rounded-2xl border-2 border-[#C9A24B] bg-[#F8F5EE] p-6 md:p-8">
              <p className="font-sans text-xs uppercase tracking-[0.25em] text-[#C9A24B] font-semibold text-center">
                J'ai rendez-vous avec mon futur
              </p>
              <h2 className="font-serif text-2xl md:text-3xl text-[#1F2A24] text-center mt-2">Dans 21 jours</h2>
              <p className="font-serif italic text-[#1F2A24]/80 text-center mt-4 max-w-xl mx-auto leading-relaxed">
                « Imagine-toi dans 21 jours. Tu viens de terminer ton parcours. Quelque chose a changé.
                Depuis ce futur, adresse-toi à la personne que tu es aujourd'hui. »
              </p>
              <ul className="mt-5 grid sm:grid-cols-2 gap-x-6 gap-y-1.5 max-w-xl mx-auto font-sans text-sm text-[#1F2A24]/70 list-disc pl-5">
                <li>Qu'est-ce qui a changé ?</li>
                <li>Qu'as-tu compris ?</li>
                <li>Qu'as-tu dépassé ?</li>
                <li>Que ressens-tu ?</li>
                <li>Qu'est-ce que tu fais différemment ?</li>
                <li>Que voudrais-tu dire à ton toi d'aujourd'hui ?</li>
              </ul>
              <div className="mt-5 rounded-xl border border-[#E8DCC4] bg-white p-4 text-center">
                <p className="font-sans text-xs uppercase tracking-[0.2em] text-[#C9A24B] font-semibold">Écouter un exemple</p>
                <p className="font-sans text-sm text-[#1F2A24]/70 mt-1">Le ton attendu pour votre message.</p>
                <audio data-testid="demo-jour1" src={demoSrc} onError={() => { if (demoSrc !== "/demo-jour1.mp3") setDemoSrc("/demo-jour1.mp3"); }} controls className="w-full max-w-sm mx-auto mt-3" />
              </div>
              <div className="mt-6">
                <VoiceRecorder userId={userId} bookId={id} slot="day1" saved={hasDay1} onSaved={refreshRec} />
              </div>
              <div className="mt-4 rounded-xl border border-[#E8DCC4] bg-white p-4">
                <label className="font-sans text-xs uppercase tracking-[0.2em] text-[#C9A24B] font-semibold">
                  Rappel du rendez-vous (facultatif)
                </label>
                <p className="font-sans text-xs text-[#1F2A24]/60 mt-1">
                  Recevez un e-mail le jour de votre rendez-vous, dans 21 jours.
                </p>
                <div className="flex flex-wrap gap-2 mt-3">
                  <input
                    data-testid="reminder-email"
                    type="email"
                    value={reminderEmail}
                    onChange={(e) => setReminderEmail(e.target.value)}
                    placeholder="votre@email.fr"
                    className="flex-1 min-w-[200px] rounded-full border border-[#E8DCC4] px-4 py-2.5 font-sans text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A24B]/40"
                  />
                  <button
                    data-testid="reminder-save"
                    onClick={saveReminder}
                    className="rounded-full bg-[#385E3A] text-white px-5 py-2.5 font-ui text-sm hover:bg-[#2c4a2e] transition-colors"
                  >
                    {reminderSaved ? "RAPPEL PROGRAMMÉ" : "RECEVOIR LE RAPPEL"}
                  </button>
                </div>
              </div>
              {hasDay1 && (
                <p className="font-sans text-xs text-[#1F2A24]/60 text-center mt-3">
                  🔒 Ton message est conservé. Tu le retrouveras au Jour 21.
                </p>
              )}
            </div>
          )}

          {current === 21 && (
            <div data-testid="rendezvous-day21" className="mt-8 rounded-2xl border-2 border-[#C9A24B] bg-[#F8F5EE] p-6 md:p-8">
              <h2 className="font-serif text-2xl md:text-3xl text-[#1F2A24] text-center">Le rendez-vous est arrivé</h2>
              <p className="font-sans text-sm text-[#1F2A24]/75 text-center mt-3 max-w-xl mx-auto leading-relaxed">
                Il y a 21 jours, tu as laissé un message à ton futur. Aujourd'hui, tu es arrivé au rendez-vous.
              </p>
              <div className="flex justify-center mt-6">
                {hasDay1 ? (
                  <AudioPlayer userId={userId} bookId={id} slot="day1" label="ÉCOUTER MON MESSAGE" />
                ) : (
                  <p className="font-sans text-sm text-[#1F2A24]/50">Aucun message du Jour 1 n'a été enregistré.</p>
                )}
              </div>
              <div className="gold-divider my-8" />
              <h3 className="font-serif text-xl md:text-2xl text-[#1F2A24] text-center">Maintenant, je lui réponds</h3>
              <p className="font-sans text-sm text-[#1F2A24]/75 text-center mt-2 max-w-xl mx-auto leading-relaxed">
                Maintenant que tu es arrivé au rendez-vous, adresse-toi à la personne que tu étais il y a 21 jours.
              </p>
              <div className="mt-5">
                <VoiceRecorder userId={userId} bookId={id} slot="day21" saved={hasDay21} onSaved={refreshRec} accent="#C9A24B" />
              </div>
              {hasDay21 && <div className="mt-8"><VoiceJourney /></div>}
            </div>
          )}

          {/* journal */}
          <div className="mt-6">
            <label className="font-sans text-xs uppercase tracking-[0.2em] text-[#C9A24B] font-semibold">
              Ma réponse
            </label>
            <textarea
              data-testid="reader-journal-input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={4}
              placeholder="Écrivez ce que vous avez compris, ressenti, expérimenté…"
              className="w-full mt-2 rounded-xl border border-[#E8DCC4] bg-white p-4 font-sans text-sm text-[#1F2A24] focus:outline-none focus:ring-2 focus:ring-[#C9A24B]/40"
            />
            <div className="flex flex-wrap gap-3 mt-3">
              <button
                data-testid="reader-save-note"
                onClick={saveNote}
                className="rounded-full border border-[#C9A24B] text-[#1F2A24] px-5 py-2.5 font-ui text-sm hover:bg-[#C9A24B]/10 transition-colors"
              >
                ENREGISTRER
              </button>
              <button
                data-testid="reader-finish-day"
                onClick={finishDay}
                className={`inline-flex items-center gap-2 rounded-full px-6 py-2.5 font-ui text-sm text-white transition-colors ${
                  isDone ? "bg-[#385E3A]/70" : "bg-[#385E3A] hover:bg-[#2c4a2e]"
                }`}
              >
                <Check size={16} /> {isDone ? "TERMINÉ" : "J'AI TERMINÉ"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Block = ({ label, text, color }) => (
  <div className="rounded-xl bg-[#F8F5EE] border border-[#E8DCC4] p-4">
    <span className="font-ui text-[11px] tracking-widest" style={{ color }}>{label}</span>
    <p className="font-sans text-sm text-[#1F2A24]/85 mt-1 leading-relaxed">{text}</p>
  </div>
);
